import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { paymentsService } from '../services/payments.service';
import { useAuth } from '../hooks/useAuth';

export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshAuth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sessionId = searchParams.get('session_id');
  const isSuccess = window.location.pathname.includes('/checkout/success');
  const isCancel = window.location.pathname.includes('/checkout/cancel');

  useEffect(() => {
    if (isSuccess && sessionId) {
      // Refresh user data to get updated premium status
      refreshAuth();
    }
  }, [isSuccess, sessionId, refreshAuth]);

  const handleStartCheckout = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { url } = await paymentsService.createCheckoutSession();
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout session');
      setIsLoading(false);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoToPremium = () => {
    navigate('/premium');
  };

  // Success view
  if (isSuccess) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.successIcon}>✓</div>
          <h1 style={styles.title}>Welcome to Premium!</h1>
          <p style={styles.message}>
            Your subscription is now active. You now have access to all premium features!
          </p>
          <ul style={styles.featureList}>
            <li>Unlimited sessions</li>
            <li>No time limits</li>
            <li>Skip to next match</li>
            <li>Match with anyone worldwide</li>
            <li>Priority matching</li>
          </ul>
          <button onClick={handleGoHome} style={styles.button}>
            Start Chatting
          </button>
        </div>
      </div>
    );
  }

  // Cancel view
  if (isCancel) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.cancelIcon}>✕</div>
          <h1 style={styles.title}>Checkout Cancelled</h1>
          <p style={styles.message}>
            No worries! You can upgrade to premium anytime.
          </p>
          <div style={styles.buttonGroup}>
            <button onClick={handleGoToPremium} style={styles.button}>
              Try Again
            </button>
            <button onClick={handleGoHome} style={styles.secondaryButton}>
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Default checkout view (shouldn't normally be shown as we redirect immediately)
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.title}>Upgrade to Premium</h1>
        <p style={styles.message}>
          Click below to proceed to secure checkout.
        </p>
        {error && <div style={styles.error}>{error}</div>}
        <button
          onClick={handleStartCheckout}
          disabled={isLoading}
          style={{
            ...styles.button,
            opacity: isLoading ? 0.6 : 1,
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {isLoading ? 'Loading...' : 'Proceed to Checkout'}
        </button>
        <button onClick={handleGoHome} style={styles.secondaryButton}>
          Cancel
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-soft-cream)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 'var(--radius-lg)',
    padding: '3rem',
    maxWidth: '500px',
    width: '100%',
    boxShadow: 'var(--shadow-lg)',
    textAlign: 'center',
  },
  successIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#10b981',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '3rem',
    margin: '0 auto 1.5rem',
    fontWeight: 'bold',
  },
  cancelIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '3rem',
    margin: '0 auto 1.5rem',
    fontWeight: 'bold',
  },
  title: {
    fontSize: '2rem',
    fontWeight: '700',
    color: 'var(--color-deep-purple)',
    marginBottom: '1rem',
  },
  message: {
    fontSize: '1.1rem',
    color: 'var(--color-gray-600)',
    marginBottom: '2rem',
    lineHeight: '1.6',
  },
  featureList: {
    textAlign: 'left',
    marginBottom: '2rem',
    paddingLeft: '1.5rem',
    color: 'var(--color-gray-700)',
  },
  button: {
    width: '100%',
    padding: '1rem',
    backgroundColor: 'var(--color-warm-orange)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '1rem',
    transition: 'background-color 0.2s',
  },
  secondaryButton: {
    width: '100%',
    padding: '1rem',
    backgroundColor: 'var(--color-gray-200)',
    color: 'var(--color-dark-navy)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  error: {
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    padding: '1rem',
    borderRadius: 'var(--radius-md)',
    marginBottom: '1rem',
    fontSize: '0.9rem',
  },
};
